import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { CampaignAiChat } from '@/features/campaignAi/CampaignAiChat'
import { ShopLauncher } from '@/features/shop/ShopLauncher'
import { getAiAvailability } from '@/features/campaignAi/api'
import aiStyles from '@/features/campaignAi/CampaignAi.module.css'
import { LandingProfileDock } from './LandingProfileDock'
import {
  LANDING_PROFILE_ACHIEVEMENTS_ID,
  LANDING_PROFILE_RANKING_HEADING_ID,
  LANDING_PROFILE_SYSTEM_ID,
  buildLandingProfileDockItems,
  scrollLandingProfileAnchor,
} from './landing-profile-dock'
import styles from './LandingNumbersPage.module.css'

export type LandingProfileTabId = 'numeros' | 'roleta' | 'cadastro'

type TabItem = {
  id: LandingProfileTabId
  label: string
  icon: string
}

const BASE_TABS: TabItem[] = [
  { id: 'numeros', label: 'Números', icon: 'mdi:ticket-outline' },
  { id: 'roleta', label: 'Roleta', icon: 'mdi:gift-outline' },
  { id: 'cadastro', label: 'Cadastro', icon: 'mdi:account-edit-outline' },
]

type LandingProfileWorkspaceProps = {
  identity:
    | ReactNode
    | ((
        onCompleteProfile: () => void,
        onViewNumbers: () => void,
        onViewAchievements: () => void
      ) => ReactNode)
  achievements?:
    | ReactNode
    | ((
        onCompleteProfile: () => void,
        onViewNumbers: () => void,
        onGoToRoulette: () => void
      ) => ReactNode)
  ranking?: ReactNode
  numbers: ReactNode
  roulette: ReactNode | null
  cadastro: ReactNode
  security: ReactNode
  homeHref: string
  rouletteOpportunityCount?: number
  requestedTab?: LandingProfileTabId
  scratch?: { visible: boolean; count: number; onOpen: () => void }
  scratchModal?: (onViewNumbers: () => void) => ReactNode
  aiCampaignId?: number
  shop?: { campaignId: number; institutionName: string }
  onAiPurchaseConfirmed?: () => void
}

export function LandingProfileWorkspace({
  identity,
  achievements,
  ranking,
  numbers,
  roulette,
  cadastro,
  security,
  homeHref,
  rouletteOpportunityCount = 0,
  requestedTab,
  scratch,
  scratchModal,
  aiCampaignId,
  shop,
  onAiPurchaseConfirmed,
}: LandingProfileWorkspaceProps) {
  const [shopOpen, setShopOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiAvailability, setAiAvailability] = useState<{
    campaignId: number
    enabled: boolean
  } | null>(null)
  const aiAvailable =
    aiAvailability?.campaignId === aiCampaignId && aiAvailability?.enabled === true
  useEffect(() => {
    if (!aiCampaignId) return
    const controller = new AbortController()
    void getAiAvailability(aiCampaignId, controller.signal)
      .then(({ enabled }) => {
        if (!controller.signal.aborted)
          setAiAvailability({ campaignId: aiCampaignId, enabled })
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setAiAvailability({ campaignId: aiCampaignId, enabled: false })
      })
    return () => controller.abort()
  }, [aiCampaignId])
  const tabs = useMemo(
    () => BASE_TABS.filter((tab) => tab.id !== 'roleta' || roulette != null),
    [roulette]
  )
  const [userTab, setUserTab] = useState<LandingProfileTabId | null>(null)
  const activeTab: LandingProfileTabId =
    userTab && tabs.some((tab) => tab.id === userTab)
      ? userTab
      : requestedTab && tabs.some((tab) => tab.id === requestedTab)
        ? requestedTab
        : 'numeros'
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === activeTab)
  )

  const goToPreviousTab = () => {
    if (tabs.length === 0) return
    const previousTab = tabs[(activeIndex - 1 + tabs.length) % tabs.length]
    if (!previousTab) return
    setUserTab(previousTab.id)
  }

  const goToNextTab = () => {
    if (tabs.length === 0) return
    const nextTab = tabs[(activeIndex + 1) % tabs.length]
    if (!nextTab) return
    setUserTab(nextTab.id)
  }

  const goToTabFromDock = (id: LandingProfileTabId) => {
    setUserTab(id)
    window.requestAnimationFrame(() => {
      scrollLandingProfileAnchor(LANDING_PROFILE_SYSTEM_ID)
    })
  }

  const goToProfileCadastro = () => {
    setUserTab('cadastro')
    window.requestAnimationFrame(() => {
      scrollLandingProfileAnchor(LANDING_PROFILE_SYSTEM_ID)
    })
  }

  const goToProfileNumbers = () => goToTabFromDock('numeros')
  const goToProfileAchievements = () =>
    scrollLandingProfileAnchor(LANDING_PROFILE_ACHIEVEMENTS_ID)

  const identityContent =
    typeof identity === 'function'
      ? identity(goToProfileCadastro, goToProfileNumbers, goToProfileAchievements)
      : identity
  const achievementsContent =
    typeof achievements === 'function'
      ? achievements(goToProfileCadastro, goToProfileNumbers, () =>
          goToTabFromDock('roleta')
        )
      : achievements

  const dockItems = buildLandingProfileDockItems({
    homeHref,
    hasRoulette: roulette != null,
    rouletteOpportunityCount,
    activeTab,
    onTab: goToTabFromDock,
    onInfo: () => scrollLandingProfileAnchor(LANDING_PROFILE_RANKING_HEADING_ID),
    onShop: shop ? () => setShopOpen(true) : undefined,
  })

  if (scratch?.visible)
    dockItems.splice(2, 0, {
      id: 'raspadinha',
      label: 'Raspadinha',
      icon: 'mdi:gesture-swipe',
      tone: 'scratch',
      accessibleName:
        scratch.count > 0
          ? `Atalho para raspadinha, ${scratch.count} disponíveis`
          : 'Atalho para raspadinha',
      opportunityCount: scratch.count,
      onSelect: scratch.onOpen,
    })

  if (aiAvailable)
    dockItems.splice(2, 0, {
      id: 'ia',
      label: 'Chat IA',
      icon: 'mdi:robot-happy-outline',
      tone: 'ai',
      accessibleName: 'Conversar com a inteligência artificial',
      onSelect: () => setAiOpen(true),
    })

  return (
    <div className={styles.workspace}>
      {aiAvailable && aiCampaignId && (
        <CampaignAiChat
          campaignId={aiCampaignId}
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          onPurchaseConfirmed={onAiPurchaseConfirmed}
        />
      )}
      {scratchModal?.(goToProfileNumbers)}
      <div className={styles.profileColumn}>
        <div className={styles.identitySpot}>{identityContent}</div>
        {shop && (
          <ShopLauncher
            {...shop}
            open={shopOpen}
            onOpen={() => setShopOpen(true)}
            onClose={() => setShopOpen(false)}
          />
        )}
        {aiAvailable && (
          <button
            type="button"
            className={aiStyles.launcher}
            onClick={() => setAiOpen(true)}
          >
            <Icon icon="mdi:robot-happy-outline" width={32} />
            <span>
              <strong>Converse com a IA</strong>
              <small>Tire dúvidas e apoie esta campanha</small>
            </span>
            <Icon icon="mdi:arrow-top-right" width={22} />
          </button>
        )}
        <div className={styles.rankingSpot}>{ranking}</div>
      </div>

      <div className={styles.profileColumn}>
        <div className={styles.achievementSpot}>{achievementsContent}</div>
        <div className={styles.system} id={LANDING_PROFILE_SYSTEM_ID}>
          <div className={styles.pager}>
            <button
              type="button"
              className={styles.pagerArrow}
              onClick={goToPreviousTab}
              aria-label="Aba anterior"
            >
              <Icon
                icon="mdi:chevron-left"
                className={styles.pagerArrowIcon}
                aria-hidden
              />
            </button>

            <div className={styles.pagerStage}>
              <div
                className={styles.pagerTrack}
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                role="tablist"
                aria-label="Áreas da conta"
              >
                {tabs.map((tab) => {
                  const isActive = tab.id === activeTab
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      id={`landing-profile-tab-${tab.id}`}
                      aria-selected={isActive}
                      aria-controls={`landing-profile-panel-${tab.id}`}
                      className={
                        isActive
                          ? `${styles.pagerItem} ${styles.pagerItemActive}`
                          : styles.pagerItem
                      }
                      onClick={() => setUserTab(tab.id)}
                      tabIndex={isActive ? 0 : -1}
                    >
                      <Icon
                        icon={tab.icon}
                        className={styles.pagerItemIcon}
                        aria-hidden
                      />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            <button
              type="button"
              className={styles.pagerArrow}
              onClick={goToNextTab}
              aria-label="Próxima aba"
            >
              <Icon
                icon="mdi:chevron-right"
                className={styles.pagerArrowIcon}
                aria-hidden
              />
            </button>
          </div>

          <div className={styles.pagerDots}>
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                className={
                  index === activeIndex
                    ? `${styles.pagerDot} ${styles.pagerDotActive}`
                    : styles.pagerDot
                }
                onClick={() => setUserTab(tab.id)}
                aria-label={`Ir para ${tab.label}`}
              />
            ))}
          </div>

          <div
            className={
              activeTab === 'numeros'
                ? `${styles.tabPanel} ${styles.tabPanelActive}`
                : styles.tabPanel
            }
            role="tabpanel"
            id="landing-profile-panel-numeros"
            aria-labelledby="landing-profile-tab-numeros"
            hidden={activeTab !== 'numeros'}
          >
            {activeTab === 'numeros' ? numbers : null}
          </div>

          {roulette != null ? (
            <div
              className={
                activeTab === 'roleta'
                  ? `${styles.tabPanel} ${styles.tabPanelActive}`
                  : styles.tabPanel
              }
              role="tabpanel"
              id="landing-profile-panel-roleta"
              aria-labelledby="landing-profile-tab-roleta"
              hidden={activeTab !== 'roleta'}
            >
              {roulette}
            </div>
          ) : null}

          <div
            className={
              activeTab === 'cadastro'
                ? `${styles.tabPanel} ${styles.tabPanelActive}`
                : styles.tabPanel
            }
            role="tabpanel"
            id="landing-profile-panel-cadastro"
            aria-labelledby="landing-profile-tab-cadastro"
            hidden={activeTab !== 'cadastro'}
          >
            <div className={styles.stack}>
              {cadastro}
              {security}
            </div>
          </div>
        </div>
      </div>

      <LandingProfileDock items={dockItems} />
    </div>
  )
}
